import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { SectionHeader } from "@/components/common/Widgets";
import { ShieldCheck, CheckCircle2, XCircle, Loader2, FileSearch, Save } from "lucide-react";
import { fmtNum } from "@/lib/format";
import useFormDraft from "@/lib/useFormDraft";
import DraftBanner from "@/components/common/DraftBanner";

export default function CustomerNewPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const draftIdParam = searchParams.get("draft");
  const [constitutions, setConstitutions] = useState(["Individual","Proprietorship","Partnership","LLP","Pvt Ltd","Public Ltd","Trust","HUF","Co-operative Society"]);
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState({
    first_name: "", middle_name: "", last_name: "",
    constitution: "Individual",
    pan: "", aadhaar_last4: "", gstin: "",
    mobile: "", email: "", address: "", branch: "",
    activity: "",
    promoter_name: "", promoter_dob: "", promoter_experience_years: 0,
    cibil_score: 0, experian_score: 0, equifax_score: 0, crif_score: 0,
    dob: "", gender: "", marital_status: "", occupation: "",
  });
  const [busy, setBusy] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [kyc, setKyc] = useState({ aadhaar: null, pan: null, gst: null });
  const [verifying, setVerifying] = useState({});
  const [bureauResult, setBureauResult] = useState(null);

  // Auto-draft + Resume (backend-synced for cross-device support)
  const draftMetaPayload = {
    display_name: `${form.first_name || ""} ${form.last_name || ""}`.trim() || "Unnamed customer draft",
    constitution: form.constitution,
  };
  const { hasDraft, draftMeta, restore, clear: clearDraft, saveNow } = useFormDraft(
    "customer-new",
    form,
    {},
    { backend: true, kind: "customer", meta: draftMetaPayload }
  );
  const [showBanner, setShowBanner] = useState(false);
  useEffect(() => { setShowBanner(hasDraft && !draftIdParam); }, [hasDraft, draftIdParam]);
  const handleResume = () => {
    const r = restore();
    if (r?.form) {
      setForm((f) => ({ ...f, ...r.form }));
      toast.success("Draft restored");
    }
    setShowBanner(false);
  };
  const handleDiscard = async () => {
    await clearDraft();
    setShowBanner(false);
    toast.info("Draft discarded");
  };

  // Deep-link resume: when navigated with /customers/new?draft=<id>, hydrate from that draft.
  useEffect(() => {
    if (!draftIdParam) return;
    (async () => {
      try {
        const r = await api.get(`/drafts/${draftIdParam}`);
        if (r?.data?.payload) {
          setForm((f) => ({ ...f, ...r.data.payload }));
          toast.success("Draft resumed from My Drafts");
        }
      } catch (_) {
        toast.error("Could not load that draft");
      }
    })();
  }, [draftIdParam]);

  useEffect(() => {
    api.get("/customers/meta/constitutions").then(r => setConstitutions(r.data)).catch(() => {});
    api.get("/master/branches").then(r => setBranches(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isIndividual = form.constitution === "Individual";

  const verify = async (kind) => {
    setVerifying((v) => ({ ...v, [kind]: true }));
    try {
      let res;
      if (kind === "aadhaar") {
        if (!form.aadhaar_last4) return toast.error("Enter Aadhaar last 4 digits");
        res = await api.post("/integrations/aadhaar/verify", { aadhaar_number: form.aadhaar_last4 });
      } else if (kind === "pan") {
        if (!form.pan) return toast.error("Enter PAN");
        res = await api.post("/integrations/pan/verify", { pan: form.pan });
      } else if (kind === "gst") {
        if (!form.gstin) return toast.error("Enter GSTIN");
        res = await api.post("/integrations/gst/verify", { gstin: form.gstin });
      }
      setKyc((k) => ({ ...k, [kind]: res.data }));
      toast.success(`${kind.toUpperCase()} verified (mocked)`);
    } catch (e) { toast.error("Verification failed"); }
    finally { setVerifying((v) => ({ ...v, [kind]: false })); }
  };

  const multiBureauPull = async () => {
    if (!form.pan) return toast.error("PAN required for multi-bureau pull");
    setVerifying((v) => ({ ...v, multiBureau: true }));
    try {
      const res = await api.post("/integrations/credit-bureaus/pull", { pan: form.pan, name: `${form.first_name} ${form.last_name}` });
      setBureauResult(res.data);
      // Auto-fill scores
      if (res.data.bureaus?.cibil) set("cibil_score", res.data.bureaus.cibil.score);
      if (res.data.bureaus?.experian) set("experian_score", res.data.bureaus.experian.score);
      if (res.data.bureaus?.equifax) set("equifax_score", res.data.bureaus.equifax.score);
      if (res.data.bureaus?.crif) set("crif_score", res.data.bureaus.crif.score);
      toast.success(`Pulled scores from ${res.data.summary.count} bureau(s)`);
    } catch (e) { toast.error("Failed"); }
    finally { setVerifying((v) => ({ ...v, multiBureau: false })); }
  };

  const handleSaveDraftAndExit = async () => {
    setSavingDraft(true);
    try {
      const result = await saveNow();
      if (result?.id) {
        toast.success("Draft saved. You can resume from My Drafts.");
      } else {
        toast.info("Draft saved locally.");
      }
      navigate("/drafts");
    } catch (e) {
      toast.error("Could not save draft");
    } finally {
      setSavingDraft(false);
    }
  };

  const save = async (e) => {
    e?.preventDefault?.();
    if (!form.first_name?.trim()) return toast.error(t("forms.firstName") + " required");
    if (!form.last_name?.trim()) return toast.error(t("forms.lastName") + " required");
    if (!form.constitution) return toast.error(t("forms.constitution") + " required");
    setBusy(true);
    try {
      // Build a clean payload — strip empty strings, coerce numeric fields safely
      const cleaned = {};
      Object.entries(form).forEach(([k, v]) => {
        if (typeof v === "string") {
          const t2 = v.trim();
          if (t2 !== "") cleaned[k] = t2;
        } else if (v !== null && v !== undefined && v !== "") {
          cleaned[k] = v;
        }
      });
      const payload = {
        ...cleaned,
        promoter_experience_years: form.promoter_experience_years ? Number(form.promoter_experience_years) : null,
        cibil_score: form.cibil_score ? Number(form.cibil_score) : null,
        experian_score: form.experian_score ? Number(form.experian_score) : null,
        equifax_score: form.equifax_score ? Number(form.equifax_score) : null,
        crif_score: form.crif_score ? Number(form.crif_score) : null,
        kyc_status: {
          aadhaar: kyc.aadhaar?.status || "pending",
          pan: kyc.pan?.status || "pending",
          gst: kyc.gst?.status || "pending",
          cibil: bureauResult ? "verified" : "pending",
        },
      };
      // Always ensure required fields are present (Pydantic strict)
      payload.first_name = form.first_name.trim();
      payload.last_name = form.last_name.trim();
      payload.constitution = form.constitution;
      const res = await api.post("/customers", payload);
      await clearDraft();  // remove the auto-saved draft on success
      toast.success(`Customer onboarded: ${res.data.cif}`);
      navigate(`/customers/new?cust=${res.data.id}`);
    } catch (err) {
      // Pydantic returns detail as an array of {loc, msg} objects — normalise
      let msg = "Failed to save customer";
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail)) {
        msg = detail.map((d) => `${(d.loc || []).slice(-1)[0] || "field"}: ${d.msg}`).join("; ");
      } else if (typeof detail === "string") {
        msg = detail;
      } else if (err?.message) {
        msg = err.message;
      }
      // eslint-disable-next-line no-console
      console.error("Customer save error:", err);
      toast.error(msg, { duration: 6000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5 max-w-5xl">
      <SectionHeader title="Onboard Customer" subtitle="Enter borrower details. External APIs (Aadhaar/PAN/GST/Credit Bureaus) are MOCKED." />

      {showBanner && draftMeta?.savedAt && (
        <DraftBanner
          savedAt={draftMeta.savedAt}
          onResume={handleResume}
          onDiscard={handleDiscard}
          label="customer draft"
        />
      )}

      <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-4 lg:col-span-2 space-y-4">
          <h3 className="text-sm font-semibold heading">Borrower Identity</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label={`${t("forms.constitution")} *`}>
              <Select value={form.constitution} onValueChange={(v) => set("constitution", v)}>
                <SelectTrigger data-testid="customer-constitution-select"><SelectValue /></SelectTrigger>
                <SelectContent>{constitutions.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Activity"><Input value={form.activity} onChange={(e) => set("activity", e.target.value)} placeholder={isIndividual ? "e.g., Salaried IT Engineer" : "e.g., Trading of steel & hardware"} /></Field>
            <Field label={`${t("forms.firstName")} *`}><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} required data-testid="customer-first-name" /></Field>
            <Field label={t("forms.middleName")}><Input value={form.middle_name} onChange={(e) => set("middle_name", e.target.value)} data-testid="customer-middle-name" /></Field>
            <Field label={`${t("forms.lastName")} *`}><Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} required data-testid="customer-last-name" /></Field>
            <Field label={t("common.branch")}>
              <Select value={form.branch} onValueChange={(v) => set("branch", v)}>
                <SelectTrigger><SelectValue placeholder="Select branch" /></SelectTrigger>
                <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.name}>{b.name} ({b.code})</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={t("forms.mobile")}><Input value={form.mobile} onChange={(e) => set("mobile", e.target.value)} /></Field>
            <Field label={t("forms.email")}><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
            <Field label={t("forms.address")} className="md:col-span-2"><Input value={form.address} onChange={(e) => set("address", e.target.value)} /></Field>
          </div>

          {isIndividual && (
            <div className="pt-3 border-t">
              <h3 className="text-sm font-semibold heading">{t("forms.individual")} Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-2">
                <Field label={t("forms.dob")}><Input type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)} /></Field>
                <Field label={t("forms.gender")}>
                  <Select value={form.gender} onValueChange={(v) => set("gender", v)}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">{t("forms.male")}</SelectItem>
                      <SelectItem value="Female">{t("forms.female")}</SelectItem>
                      <SelectItem value="Other">{t("forms.other")}</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={t("forms.maritalStatus")}>
                  <Select value={form.marital_status} onValueChange={(v) => set("marital_status", v)}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Single">{t("forms.single")}</SelectItem>
                      <SelectItem value="Married">{t("forms.married")}</SelectItem>
                      <SelectItem value="Divorced">Divorced</SelectItem>
                      <SelectItem value="Widowed">Widowed</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={t("forms.occupation")}><Input value={form.occupation} onChange={(e) => set("occupation", e.target.value)} placeholder="e.g., Software Engineer, Doctor" /></Field>
              </div>
            </div>
          )}

          {!isIndividual && (
            <div className="pt-3 border-t">
              <h3 className="text-sm font-semibold heading">Promoter / Authorized Signatory</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
                <Field label="Promoter Name"><Input value={form.promoter_name} onChange={(e) => set("promoter_name", e.target.value)} /></Field>
                <Field label="DOB"><Input type="date" value={form.promoter_dob} onChange={(e) => set("promoter_dob", e.target.value)} /></Field>
                <Field label="Experience (yrs)"><Input type="number" min={0} value={form.promoter_experience_years} onChange={(e) => set("promoter_experience_years", e.target.value)} /></Field>
              </div>
            </div>
          )}
        </Card>

        <Card className="p-4 space-y-3 h-fit">
          <h3 className="text-sm font-semibold heading flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-success" />KYC Verification <Badge variant="outline" className="text-[10px]">MOCKED</Badge></h3>
          <KycRow label={`${t("forms.aadhaar")} (last 4)`} value={form.aadhaar_last4} onChange={(v) => set("aadhaar_last4", v)} onVerify={() => verify("aadhaar")} status={kyc.aadhaar?.status} pending={verifying.aadhaar} testid="kyc-aadhaar" />
          <KycRow label={t("forms.pan")} value={form.pan} onChange={(v) => set("pan", v.toUpperCase())} onVerify={() => verify("pan")} status={kyc.pan?.status} pending={verifying.pan} testid="kyc-pan" />
          {!isIndividual && <KycRow label={t("forms.gstin")} value={form.gstin} onChange={(v) => set("gstin", v.toUpperCase())} onVerify={() => verify("gst")} status={kyc.gst?.status} pending={verifying.gst} testid="kyc-gst" />}
          <div className="pt-2 border-t">
            <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2 flex items-center gap-1"><FileSearch className="h-3.5 w-3.5" />{t("forms.creditPull")}</div>
            <Button type="button" variant="secondary" disabled={verifying.multiBureau} onClick={multiBureauPull} className="w-full" data-testid="multi-bureau-pull-button">
              {verifying.multiBureau ? <Loader2 className="h-4 w-4 animate-spin" /> : "Pull from CIBIL / Experian / Equifax / CRIF"}
            </Button>
            {bureauResult && (
              <div className="mt-2 space-y-1.5 text-xs">
                <div className="text-muted-foreground">Enabled by bank: {bureauResult.enabled_bureaus.join(", ")}</div>
                <div className="grid grid-cols-2 gap-1.5">
                  {Object.entries(bureauResult.bureaus).map(([b, v]) => (
                    <div key={b} className="rounded border px-2 py-1.5 bg-muted/30">
                      <div className="text-[10px] uppercase font-medium">{b}</div>
                      <div className="text-sm font-semibold mono">{v.score} <span className="text-[10px] font-normal text-muted-foreground">{v.score_band}</span></div>
                    </div>
                  ))}
                </div>
                <div className="text-muted-foreground mono">Consensus: <b className="text-foreground">{bureauResult.summary.consensus_band}</b> (avg {bureauResult.summary.average})</div>
              </div>
            )}
          </div>
        </Card>

        <div className="lg:col-span-3 flex justify-end gap-2 flex-wrap">
          <Button variant="ghost" type="button" onClick={() => navigate(-1)}>{t("app.cancel")}</Button>
          <Button
            variant="secondary"
            type="button"
            onClick={handleSaveDraftAndExit}
            disabled={savingDraft || busy}
            data-testid="customer-save-draft-button"
          >
            {savingDraft ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            Save Draft & Exit
          </Button>
          <Button type="submit" disabled={busy} data-testid="customer-save-button">{busy ? t("app.loading") : t("forms.addCustomer")}</Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (<div className={`space-y-1.5 ${className}`}><Label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">{label}</Label>{children}</div>);
}

function KycRow({ label, value, onChange, onVerify, status, pending, testid }) {
  return (
    <div className="flex items-end gap-2">
      <Field label={label} className="flex-1"><Input value={value} onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-input`} /></Field>
      <Button type="button" variant="secondary" disabled={pending} onClick={onVerify} data-testid={`${testid}-verify`}>{pending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}</Button>
      {status && (
        <div className={`flex items-center px-2 py-2 rounded-md text-xs ${status === "verified" || status === "active" || status === "success" ? "bg-success/10 text-success" : "bg-warning/10 text-warning"}`}>
          {status === "verified" || status === "active" || status === "success" ? <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> : <XCircle className="h-3.5 w-3.5 mr-1" />}
          {status}
        </div>
      )}
    </div>
  );
}
